'use client';

import clsx from 'clsx';
import { ChangeEvent, ReactNode, useState } from 'react';

type Props = {
 children: ReactNode;
 defaultValue: string;
 label: string;
};

export default function LocaleSwitcherSelect({
 children,
 defaultValue,
 label
}: Props) {
 const [isPending, setIsPending] = useState(false);

 function onSelectChange(event: ChangeEvent<HTMLSelectElement>) {
  const nextLocale = event.target.value as 'en' | 'de';
  
  // Set pending state for visual feedback
  setIsPending(true);
  
  // Get current URL parts
  const currentUrl = window.location.pathname;
  const urlParts = currentUrl.split('/').filter(part => part); // Remove empty parts
  const currentLocale = urlParts[0]; // First part should be locale
  
  // Path mapping for localized routes
  const pathMappings: Record<string, Record<string, string>> = {
    'de': {
      'auftrag': 'order',
      'auftrag/adressen': 'order/addresses',
      'auftrag/bestaetigung': 'order/confirm',
      'auftrag/kontakt': 'order/contact',
      'auftrag/rechnung': 'order/invoice',
      'auftrag/estrichprobe': 'order/screedcheck',
      'auftrag/danke': 'order/thanks',
      'termin': 'appointment',
      'wartung': 'maintenance',
      'einverstaendnis': 'consent'
    },
    'en': {
      'order': 'auftrag',
      'order/addresses': 'auftrag/adressen',
      'order/confirm': 'auftrag/bestaetigung',
      'order/contact': 'auftrag/kontakt',
      'order/invoice': 'auftrag/rechnung',
      'order/screedcheck': 'auftrag/estrichprobe',
      'order/thanks': 'auftrag/danke',
      'appointment': 'termin',
      'maintenance': 'wartung',
      'consent': 'einverstaendnis'
    }
  }
  
  let newPath = '';
  if (currentLocale === 'de' || currentLocale === 'en') {
    // Get path without locale
    const pathWithoutLocale = urlParts.slice(1).join('/');
    
    // Map path to target locale if mapping exists
    const mappedPath = pathMappings[currentLocale]?.[pathWithoutLocale];
    newPath = mappedPath || pathWithoutLocale;
  } else {
    // If no locale detected, use current path as-is
    newPath = urlParts.join('/');
  }
  
  // Construct new URL
  const newUrl = `/${nextLocale}${newPath ? '/' + newPath : ''}`;
  
  // Immediate navigation with full page reload for instant language switch
  window.location.href = newUrl;
 }

 return (
  <label
   className={clsx(
    'relative text-gray-400',
    isPending && 'transition-opacity [&:disabled]:opacity-30'
   )}
  >
   <p className="sr-only">{label}</p>
   <select
    className="inline-flex appearance-none bg-transparent py-1 pl-2 pr-6 text-white"
    defaultValue={defaultValue}
    disabled={isPending}
    onChange={onSelectChange}
   >
    {children}
   </select>
   <span className="pointer-events-none absolute right-2 text-white">⌄</span>
  </label>
 );
}